import React, { useCallback } from 'react';
import { styled } from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputBase from '@mui/material/InputBase';
import InputAdornment from '@mui/material/InputAdornment';
import { ProjectsOrderBy } from '@/contracts';
import AscOrder from '@/components/icons/AscOrder';
import DescOrder from '@/components/icons/DescOrder';
import t from '@/utils/translate';

type Props = {
  orderBy: ProjectsOrderBy,
  isDescOrder: boolean,
  onChange: (orderBy: ProjectsOrderBy, isDescOrder: boolean) => Promise<void>
};

const Adornment = styled(InputAdornment)({
  color: 'inherit',
  marginRight: '0px',
});

const ToggleDirectionButton = styled('button')({
  cursor: 'pointer',
  width: '28px',
  height: '28px',
  padding: '2px',
  background: 'transparent',
  border: 'none',
  color: 'inherit',
  borderRight: '1px solid #EBECF0',
  '&:hover': {
    background: '#EBECF0',
    color: '#0065FF',
  },
  '& .MuiSvgIcon-root': {
    verticalAlign: 'middle',
    fontSize: '24px',
  }
});

const OrderSelect = styled(Select)({
  padding: 0,
  border: '1px solid #DFE1E6',
  borderRadius: '6px',
  '& .MuiSelect-icon': {
    display: 'none'
  },
  '& .MuiSelect-select.MuiInputBase-input': {
    padding: '6.5px 8px !important',
    color: '#091E42',
    fontSize: '13px',
    lineHeight: '15px',
    fontWeight: 400,
    height: 'auto',
    minHeight: 'auto',
    borderRadius: 0,
    '&:hover, &[aria-expanded=true]': {
      background: '#EBECF0',
      color: '#0065FF',
    }
  },
});

export default ({ orderBy, isDescOrder, onChange }: Props) => {
  const onSelectOrderCallback = useCallback(
    (e: SelectChangeEvent<ProjectsOrderBy>) => onChange(e.target.value as ProjectsOrderBy, isDescOrder),
    [ onChange, isDescOrder ]
  );
  
  const toggleDirectionCallback = useCallback(
    () => onChange(orderBy, !isDescOrder),
    [ onChange, orderBy, isDescOrder ]
  );

  return <OrderSelect
    value={orderBy}
    onChange={onSelectOrderCallback}
    input={<InputBase />}
    startAdornment={
      <Adornment position="start">
        <ToggleDirectionButton
          onClick={toggleDirectionCallback}
          title={t('action.sortDir')}
        >
          {isDescOrder ? <DescOrder/> : <AscOrder/>}
        </ToggleDirectionButton>
      </Adornment>
    }
  >
    <MenuItem value={ProjectsOrderBy.NAME}>{t('project.sort.by.name')}</MenuItem>
    <MenuItem value={ProjectsOrderBy.CREATED}>{t('project.sort.by.creation')}</MenuItem>
    <MenuItem value={ProjectsOrderBy.UPDATED}>{t('project.sort.by.update')}</MenuItem>
  </OrderSelect>
};